import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import BackToTop from '../components/layout/BackToTop';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import aboutContent from '../content/about/index.json';
import ContactSection from '../components/sections/ContactSection';
import { usePageMeta } from '../lib/usePageMeta';
import * as Icons from 'lucide-react';
import { Mail, ArrowRight, Quote } from 'lucide-react';

const getIcon = (iconName) => {
  const IconComponent = Icons[iconName];
  return IconComponent ? <IconComponent className="w-6 h-6" /> : null;
};

const AboutPage = () => {
  usePageMeta(
    'About ResiAX — Shakirah Akinwale, Teqie Ltd',
    'Why Shakirah Akinwale founded ResiAX: a resident-owned record, practical AI support, and a clearer way for landlords and housing providers to listen and act.'
  );

  React.useEffect(() => {
    if (window.location.hash) {
      setTimeout(() => {
        document.querySelector(window.location.hash)?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  const d = aboutContent;

  return (
    <div className="min-h-screen bg-[#FFFAF8]">
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#B5541B] mb-4">
              <span className="w-8 h-px bg-[#B5541B]" />
              {d.hero.badge}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1F1E1E] leading-tight mb-6" data-testid="about-heading">
              {d.hero.title}
            </h1>
            <p className="text-lg text-[#3B3535]/75 leading-relaxed max-w-2xl">
              {d.hero.subtitle}
            </p>
          </div>
        </section>

        {/* Founder */}
        <section className="py-16 bg-[#FFFAF8]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-gray-100" data-testid="founder-story">
              <div className="text-xs uppercase tracking-widest font-bold text-[#B5541B] mb-3">{d.founder.badge}</div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1F1E1E] mb-4">{d.founder.heading}</h2>
              {(d.founder.paragraphs || []).map((p, i) => (
                <p key={i} className="text-[#3B3535] leading-relaxed mb-4 last:mb-0">{p}</p>
              ))}
              <div className="mt-8 bg-[#FFF8F5] border-l-4 border-[#B5541B] rounded-r-xl p-6">
                <Quote className="w-6 h-6 text-[#B5541B]/40 mb-3" />
                <p className="text-[#1F1E1E] leading-relaxed italic">
                  &ldquo;{d.founder.quote}&rdquo;
                </p>
                <p className="mt-4 text-sm font-semibold text-[#B5541B]">{d.founder.attribution}</p>
              </div>
            </div>
          </div>
        </section>

        {/* The problem */}
        <section className="py-16 bg-[#1F1E1E] text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6">{d.problem.heading}</h2>
            <div className="space-y-4 text-white/75 leading-relaxed">
              {(d.problem.paragraphs || []).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </section>

        {/* Rebuilding trust — Resident Action Exchange */}
        {d.bridge && (
          <section className="py-16 bg-[#FFFAF8]" data-testid="about-bridge-section">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-10">
                <span className="inline-block bg-[#B5541B]/10 text-[#B5541B] text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
                  {d.bridge.badge}
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#1F1E1E]">{d.bridge.heading}</h2>
              </div>
              <div className="space-y-4 text-[#3B3535]/80 leading-relaxed mb-10">
                {(d.bridge.paragraphs || []).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              <div className="grid md:grid-cols-2 gap-5 mb-8">
                <div className="bg-white rounded-2xl p-7 border border-[#B5541B]/10 shadow-sm" data-testid="bridge-residents-card">
                  <h3 className="font-bold text-[#1F1E1E] mb-2">{d.bridge.residents?.title}</h3>
                  <p className="text-sm text-[#3B3535]/75 leading-relaxed">{d.bridge.residents?.body}</p>
                </div>
                <div className="bg-white rounded-2xl p-7 border border-[#B5541B]/10 shadow-sm" data-testid="bridge-providers-card">
                  <h3 className="font-bold text-[#1F1E1E] mb-2">{d.bridge.providers?.title}</h3>
                  <p className="text-sm text-[#3B3535]/75 leading-relaxed">{d.bridge.providers?.body}</p>
                </div>
              </div>
              <p className="text-center text-[#1F1E1E] font-semibold max-w-2xl mx-auto" data-testid="bridge-closing">
                {d.bridge.closing}
              </p>
            </div>
          </section>
        )}

        {/* What we believe */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1F1E1E] text-center mb-10">{d.beliefs.heading}</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {(d.beliefs.items || []).map((b) => (
                <div key={b.title} className="bg-[#FFF8F5] rounded-2xl p-7 border border-[#B5541B]/10">
                  <div className="w-12 h-12 rounded-xl bg-[#B5541B]/10 flex items-center justify-center text-[#B5541B] mb-4">
                    {getIcon(b.icon)}
                  </div>
                  <h3 className="font-bold text-[#1F1E1E] mb-2">{b.title}</h3>
                  <p className="text-sm text-[#3B3535]/75 leading-relaxed">{b.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#FFFAF8]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1F1E1E] mb-4">{d.cta.heading}</h2>
            <p className="text-[#3B3535]/70 mb-8 max-w-xl mx-auto">{d.cta.description}</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button
                asChild
                className="bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold px-7 py-5 rounded-full shadow-md"
                data-testid="about-contact-cta"
              >
                <a href={d.cta.primaryHref}>
                  <Mail className="mr-2 w-4 h-4" />
                  {d.cta.primaryLabel}
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-2 border-[#B5541B] text-[#B5541B] hover:bg-[#B5541B]/10 font-semibold px-7 py-5 rounded-full"
                data-testid="about-providers-cta"
              >
                <Link to="/providers">
                  {d.cta.secondaryLabel}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
            <p className="mt-6 text-sm text-[#3B3535]/70">
              Facing a housing issue and wondering who can stand beside you?{' '}
              <Link to="/residents#support" data-testid="about-support-link" className="font-semibold text-[#B5541B] hover:text-[#8B3F14] transition-colors">
                Learn about resident support
              </Link>
            </p>
          </div>
        </section>

        {/* Contact form (moved from homepage — reachable at /about#contact) */}
        <ContactSection />
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

export default AboutPage;
